import { Fork, Invoke } from '@swf/components/StarsWizard/Flow';
import useWizardExit from '@swf/core/hooks/useWizardExit';
import type { FlowConfig } from '@swf/core/types/Wizard';
import { Action } from '@swf/core/types/Wizard';

import SignupOffer from '../../Signup/components/SignupOffer/SignupOffer';
import RMFAccumulatedBuyInLimit from '../components/RMFAccumulatedBuyInLimit/RMFAccumulatedBuyInLimit';
import RMFAddress from '../components/RMFAddress/RMFAddress';
import RMFAutomaticWithdrawalLimit from '../components/RMFAutomaticWithdrawalLimit/RMFAutomaticWithdrawalLimit';
import RMFDOB from '../components/RMFDOB/RMFDOB';
import RMFDepositLimit from '../components/RMFDepositLimit/RMFDepositLimit';
import RMFEKYCSuccess from '../components/RMFEKYCSuccess/RMFEKYCSuccess';
import RMFGender from '../components/RMFGender/RMFGender';
import RMFLegalName from '../components/RMFLegalName/RMFLegalName';
import RMFPlaceOfBirth from '../components/RMFPlaceOfBirth/RMFPlaceOfBirth';
import RMFPokerTimeLimit from '../components/RMFPokerTimeLimit/RMFPokerTimeLimit';
import RMFSummaryScreen from '../components/RMFSummaryScreen/RMFSummaryScreen';
import RMFVerification from '../components/RMFVerification/RMFVerification';

import { Guard } from './useRmfGuards';
import { Service } from './useRmfServices';

const useRmfFRFlow = (): FlowConfig => {
  const exitWizard = useWizardExit();

  const jumpToFeatureStep = {
    name: 'jumpToFeatureStep',
    entry: Action.GoJump
  };

  const invokeRmfDobUpdate = new Invoke(Service.RmfDobUpdate, RMFGender, RMFDOB);

  const forkDobCheck = new Fork(
    Guard.RmfDobEnabled,
    [RMFDOB, invokeRmfDobUpdate, RMFGender],
    [RMFGender]
  );
  const invokeSignupDob = new Invoke(Service.SignupDobFetch, forkDobCheck);

  const forkSignupDepositCheckDone = new Fork(
    Guard.SignupDepositEnabled,
    [{ name: 'rmfExit', entry: Action.GoNext }],
    [SignupOffer]
  );

  const forkJumpToFeature = new Fork(
    Guard.DoesJumpToFeatureStep,
    [new Invoke(Service.SetJumpCreator, jumpToFeatureStep)],
    [RMFVerification, forkSignupDepositCheckDone]
  );

  const forkRmfExit = new Fork(
    Guard.StandaloneRmf,
    [
      {
        name: 'exitRMF',
        entry: exitWizard,
        type: 'final'
      }
    ],
    [forkJumpToFeature]
  );
  const invokeLegalNameSave = new Invoke(Service.RmfLegalNameSave, invokeSignupDob, RMFLegalName);
  const invokeGenderSave = new Invoke(Service.RmfGenderSave, RMFPlaceOfBirth, RMFGender);

  const invokePlaceOfBirthSave = new Invoke(
    Service.RmfPlaceOfBirthSave,
    RMFAddress,
    RMFPlaceOfBirth
  );
  const invokeAddressSave = new Invoke(Service.RmfAddressSave, RMFDepositLimit, RMFAddress);

  const invokeDepositLimitSave = new Invoke(
    Service.RmfDepositLimitSave,
    RMFAccumulatedBuyInLimit,
    RMFDepositLimit
  );

  const invokeAccumulatedLimitSave = new Invoke(
    Service.RmfAccumulatedLimitSave,
    RMFPokerTimeLimit,
    RMFAccumulatedBuyInLimit
  );

  const invokePokerTimeSave = new Invoke(
    Service.RmfPokerTimeSave,
    RMFAutomaticWithdrawalLimit,
    RMFPokerTimeLimit
  );

  const invokeRetryEnableRealMoneyFlag = new Invoke(
    Service.RmfRetryInvokeRealMoneyOk,
    forkJumpToFeature
  );

  const invokeRetryFRDataSave = new Invoke(
    Service.RmfRetryFRUserDataSave,
    invokeRetryEnableRealMoneyFlag
  );

  const forkSummaryScreen = new Fork(
    Guard.IsRetryScreenEnabled,
    [RMFSummaryScreen, invokeRetryFRDataSave],
    [forkJumpToFeature]
  );

  const forkRetryScreen = new Fork(
    Guard.CheckRetryUserFlags,
    [forkSummaryScreen],
    [RMFEKYCSuccess, forkSignupDepositCheckDone]
  );
  const invokeEnableRealMoneyFlag = new Invoke(Service.RmfEnableRealMoneyFlag, forkRetryScreen);

  const invokeAutomaticWithdrawalLimitSave = new Invoke(
    Service.RmfAutomaticWithdrawalLimitSave,
    invokeEnableRealMoneyFlag,
    RMFAutomaticWithdrawalLimit
  );

  return [
    RMFLegalName,
    invokeLegalNameSave,
    invokeSignupDob,
    forkDobCheck,
    invokeGenderSave,
    RMFPlaceOfBirth,
    invokePlaceOfBirthSave,
    RMFAddress,
    invokeAddressSave,
    RMFDepositLimit,
    invokeDepositLimitSave,
    RMFAccumulatedBuyInLimit,
    invokeAccumulatedLimitSave,
    RMFPokerTimeLimit,
    invokePokerTimeSave,
    RMFAutomaticWithdrawalLimit,
    invokeAutomaticWithdrawalLimitSave,
    invokeEnableRealMoneyFlag,
    forkRetryScreen,
    invokeRetryEnableRealMoneyFlag,
    forkRmfExit,
    jumpToFeatureStep
  ];
};

export default useRmfFRFlow;
